import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { createRequest, getSites } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, Euro, Truck, MapPin, Calculator } from 'lucide-react';

import { fmt2, pricePerUnit, calcLaufmeter, isSqmUnit } from '@/lib/utils';

const INCOTERMS = ['EXW', 'FCA', 'CPT', 'CIP', 'DAP', 'DDP'];

export const RequestMaterialDialog = ({ open, onOpenChange, sku, material }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [sites, setSites] = useState([]);
  const [formData, setFormData] = useState({
    quantity: '',
    message: '',
    bid_price_EUR: '',
    book_value_EUR: '',
    incoterm: '',
    delivery_site_id: ''
  });

  useEffect(() => {
    if (open && sku) {
      loadSites();
      const sellingPrice = sku?.book_value_kEUR ? Math.round(sku.book_value_kEUR * 1000 * 100) / 100 : '';
      setFormData({
        quantity: sku?.quantity ? String(sku.quantity) : '',
        message: '',
        bid_price_EUR: sellingPrice !== '' ? String(sellingPrice) : '',
        book_value_EUR: sellingPrice !== '' ? String(sellingPrice) : '',
        incoterm: '',
        delivery_site_id: user?.site_id || ''
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sku, user]);

  const loadSites = async () => {
    try {
      const res = await getSites();
      const allSites = res.data || [];
      const role = user?.role;
      const userSiteId = user?.site_id;
      const userGroupId = user?.group_id;

      let availableSites = [];
      if (role === 'group_admin') {
        // Group admin sees all sites of their own group only
        availableSites = userGroupId ? allSites.filter(s => s.group_id === userGroupId) : [];
      } else if (role === 'admin') {
        // Platform admin: with group_id restricted to that group; without group_id sees all
        availableSites = userGroupId ? allSites.filter(s => s.group_id === userGroupId) : allSites;
      } else if (userSiteId) {
        // site_manager / user / external bound to a single site
        availableSites = allSites.filter(s => s.site_id === userSiteId);
      } else if (userGroupId) {
        // Fallback: user with group but no site
        availableSites = allSites.filter(s => s.group_id === userGroupId);
      }
      setSites(availableSites);
    } catch {
      setSites([]);
    }
  };

  const isInternal = user?.group_id && material?.group_id && user.group_id === material.group_id;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.quantity || parseFloat(formData.quantity) <= 0) {
      toast.error('Bitte geben Sie eine gultige Menge ein');
      return;
    }
    if (parseFloat(formData.quantity) > sku.quantity) {
      toast.error('Angefragte Menge uberschreitet den verfugbaren Bestand');
      return;
    }
    setLoading(true);
    try {
      await createRequest({
        sku_id: sku.sku_id,
        requested_qty: parseFloat(formData.quantity),
        message: formData.message || null,
        bid_price_EUR: formData.bid_price_EUR ? parseFloat(formData.bid_price_EUR) : null,
        book_value_EUR: formData.book_value_EUR ? parseFloat(formData.book_value_EUR) : null,
        incoterm: formData.incoterm || null,
        delivery_site_id: formData.delivery_site_id || null
      });
      toast.success('Anfrage erfolgreich gesendet');
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to create request:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Senden der Anfrage');
    } finally {
      setLoading(false);
    }
  };

  if (!sku || !material) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="request-material-dialog">
        <DialogHeader>
          <DialogTitle className="font-manrope">Material anfragen</DialogTitle>
          <DialogDescription>
            {material.common_title} - {sku.article_number}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Stock Info */}
          <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
            <p className="text-xs text-muted-foreground">Verfugbarer Bestand</p>
            <p className="text-lg font-semibold">{sku.quantity} {sku.uom}</p>
            {isSqmUnit(sku.uom) && material?.material_width_mm > 0 && sku.quantity > 0 && (
              <p className="text-sm text-muted-foreground mt-1" data-testid="request-laufmeter-info">
                = {fmt2(calcLaufmeter(sku.quantity, material.material_width_mm))} lfm
                {sku.number_of_rolls > 0 && ` (${fmt2(calcLaufmeter(sku.quantity, material.material_width_mm) / sku.number_of_rolls)} lfm/Rolle)`}
              </p>
            )}
          </div>

          {/* Request type */}
          <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm">
            <span className="font-medium">{isInternal ? 'Interne Anfrage' : 'Externe Anfrage'}</span>
            <span className="text-muted-foreground ml-2">
              {isInternal ? '(innerhalb Ihrer Gruppe)' : '(an eine andere Gruppe)'}
            </span>
          </div>

          {/* Quantity */}
          <div className="space-y-2">
            <Label htmlFor="quantity">Menge ({sku.uom}) *</Label>
            <Input
              id="quantity"
              type="number"
              min="0"
              max={sku.quantity}
              step="0.01"
              value={formData.quantity}
              onChange={(e) => setFormData(prev => ({ ...prev, quantity: e.target.value }))}
              placeholder={`Max: ${sku.quantity}`}
              required
              data-testid="request-quantity-input"
            />
          </div>

          {/* Selling Price - Pre-filled from system book value, editable */}
          <div className="space-y-2">
            <Label htmlFor="selling_price" className="flex items-center gap-1">
              <Euro className="h-4 w-4 text-orange-500" />
              Verkaufspreis (EUR)
            </Label>
            <Input
              id="selling_price"
              type="number"
              min="0"
              step="0.01"
              value={formData.bid_price_EUR}
              onChange={(e) => setFormData(prev => ({ ...prev, bid_price_EUR: e.target.value, book_value_EUR: e.target.value }))}
              placeholder="Verkaufspreis"
              data-testid="request-selling-price-input"
            />
            <p className="text-xs text-muted-foreground">Vorausgefüllt aus dem System, änderbar</p>
          </div>

          {/* Price per unit - calculated dynamically */}
          {formData.bid_price_EUR && formData.quantity && parseFloat(formData.quantity) > 0 && (
            <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg flex items-center gap-2" data-testid="request-price-per-unit">
              <Calculator className="h-4 w-4 text-orange-500" />
              <span className="text-sm font-medium">
                Preis je Einheit: {fmt2(pricePerUnit(parseFloat(formData.bid_price_EUR), parseFloat(formData.quantity)))} EUR/{sku.uom}
              </span>
            </div>
          )}

          <Separator />

          {/* Incoterm */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1">
              <Truck className="h-4 w-4" />
              Incoterm
            </Label>
            <Select value={formData.incoterm} onValueChange={(v) => setFormData(prev => ({ ...prev, incoterm: v }))}>
              <SelectTrigger data-testid="request-incoterm-select">
                <SelectValue placeholder="Incoterm auswahlen" />
              </SelectTrigger>
              <SelectContent>
                {INCOTERMS.map(ic => (
                  <SelectItem key={ic} value={ic}>{ic}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Delivery Site */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1">
              <MapPin className="h-4 w-4" />
              Lieferstandort
            </Label>
            <Select value={formData.delivery_site_id} onValueChange={(v) => setFormData(prev => ({ ...prev, delivery_site_id: v }))}>
              <SelectTrigger data-testid="request-delivery-site-select">
                <SelectValue placeholder="Lieferstandort auswahlen" />
              </SelectTrigger>
              <SelectContent>
                {sites.map(s => (
                  <SelectItem key={s.site_id} value={s.site_id}>
                    {s.site_name} ({s.city})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Message */}
          <div className="space-y-2">
            <Label htmlFor="message">Nachricht</Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
              placeholder="Hinweise oder Anforderungen..."
              rows={2}
              data-testid="request-message-input"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button type="submit" className="bg-orange-500 hover:bg-orange-600" disabled={loading} data-testid="submit-request-btn">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Anfrage senden
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
